import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { NAV_ITEMS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { usePrivacy } from "@/context/PrivacyContext";
import { Button } from "@/components/ui/button";
import { AlertsBell } from "@/components/AlertsBell";
import { DataFreshnessBadge } from "@/components/DataFreshnessBadge";
import { UpdatingTag } from "@/components/UpdatingTag";
import { useIssues } from "@/lib/useIssues";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Moon, Sun, LogOut, ChevronDown, Eye, EyeOff } from "lucide-react";

function Brand() {
    return (
        <div className="flex items-center gap-2.5 px-0 sm:px-2" data-testid="app-brand">
            <div className="rounded-lg bg-[#f7efe8] flex items-center justify-center shrink-0 px-2 py-1.5">
                <img src="/moringa-logo.webp" alt="Moringa Finance" className="h-6 w-auto object-contain" />
            </div>
            <div className="hidden sm:block lg:block text-[10px] uppercase tracking-widest text-muted-foreground">Finance</div>
        </div>
    );
}

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const { hidden, toggle: togglePrivacy } = usePrivacy();
    const navigate = useNavigate();
    const location = useLocation();
    const primary = NAV_ITEMS.filter((n) => n.primary);
    const secondary = NAV_ITEMS.filter((n) => !n.primary);
    const current = NAV_ITEMS.find((n) => n.to === location.pathname);
    const { data: issueData } = useIssues();
    const issueCount = issueData?.count || 0;
    const badge = (item) => item.to === "/issues" && issueCount > 0 && (
        <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-loss text-white text-[10px] font-semibold flex items-center justify-center num"
            data-testid="issues-nav-badge">{issueCount}</span>
    );

    const handleLogout = async () => { await logout(); navigate("/login"); };

    return (
        <div className="min-h-screen bg-background">
            {/* Desktop sidebar */}
            <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 border-r border-border bg-card/40 backdrop-blur-sm z-40">
                <div className="h-16 flex items-center border-b border-border px-4"><Brand /></div>
                <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                    {NAV_ITEMS.map((item) => {
                        const Icon = item.icon;
                        return (
                            <NavLink key={item.to} to={item.to} end={item.to === "/"}
                                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                        isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                                    }`}>
                                <Icon className="w-[18px] h-[18px]" />
                                {item.label}
                                {badge(item)}
                            </NavLink>
                        );
                    })}
                </nav>
                <div className="p-3 border-t border-border">
                    <div className="flex items-center gap-2 px-2 py-1.5">
                        <div className="w-8 h-8 rounded-full bg-accent/20 text-accent flex items-center justify-center text-xs font-semibold uppercase">
                            {(user?.name || user?.email || "?")[0]}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{user?.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                        </div>
                        <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn" title="Log out">
                            <LogOut className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </aside>

            {/* Top bar */}
            <header className="lg:pl-64 sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-md" style={{ paddingTop: "env(safe-area-inset-top)" }}>
                <div className="h-16 px-3 md:px-6 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="lg:hidden"><Brand /></div>
                        <h1 className="hidden lg:block font-heading text-lg font-semibold">{current?.label || "Moringa Finance"}</h1>
                    </div>
                    <div className="flex items-center gap-0.5 sm:gap-2 shrink-0">
                        <UpdatingTag />
                        <DataFreshnessBadge />
                        <AlertsBell />
                        <Button variant="ghost" size="icon" onClick={togglePrivacy} data-testid="privacy-toggle-btn"
                            title={hidden ? "Show values" : "Hide values"}
                            className={hidden ? "text-primary" : ""}>
                            {hidden ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle-btn" title="Toggle theme">
                            {theme === "dark" ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
                        </Button>
                        <div className="lg:hidden">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="gap-1" data-testid="mobile-menu-btn">
                                        More {issueCount > 0 && <span className="w-2 h-2 rounded-full bg-loss" />}<ChevronDown className="w-4 h-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                    {secondary.map((item) => (
                                        <DropdownMenuItem key={item.to} onClick={() => navigate(item.to)}>
                                            <item.icon className="w-4 h-4 mr-2" /> {item.label}{badge(item)}
                                        </DropdownMenuItem>
                                    ))}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout} data-testid="mobile-logout">
                                        <LogOut className="w-4 h-4 mr-2" /> Log out
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main */}
            <main className="lg:pl-64 pb-24 lg:pb-8">
                <div className="px-4 md:px-6 py-6 max-w-[1600px] mx-auto animate-fade-in-up">{children}</div>
            </main>

            {/* Mobile bottom nav */}
            <nav className="lg:hidden fixed bottom-0 inset-x-0 h-16 z-50 border-t border-border bg-background/95 backdrop-blur-md flex justify-around items-center" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
                {primary.map((item) => {
                    const Icon = item.icon;
                    return (
                        <NavLink key={item.to} to={item.to} end={item.to === "/"}
                            data-testid={`bottomnav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                            className={({ isActive }) =>
                                `flex flex-col items-center justify-center gap-0.5 flex-1 h-full text-[10px] font-medium transition-colors ${
                                    isActive ? "text-primary" : "text-muted-foreground"
                                }`}>
                            <Icon className="w-5 h-5" />
                            {item.label}
                        </NavLink>
                    );
                })}
            </nav>
        </div>
    );
}
